import { serverInfo } from "../config/serverConfig";
import { checkResponse } from "../utils/apiError";


import { apiFetch } from "./apiFetch";

/**
 * Fetch tasks for a given account_id. For non-Admin/Super Admin, only tasks created by or assigned to the user are returned.
 * @param {string} accountId
 * @param {{ user_id?: string, role?: string }} [options] - pass user_id and role for role-based filtering
 */
export async function fetchTasksForAccount(accountId, options = {}) {
  if (!accountId) return [];

  const body = {
    account_id: accountId,
    user_id: options.user_id != null ? String(options.user_id) : "",
    role: options.role != null ? String(options.role) : "",
  };
  const resp = await apiFetch(serverInfo.serverUrl + "/getTask", {
    method: "POST",
    body: JSON.stringify(body),
  });

  checkResponse(resp);
  const data = await resp.json();
  return Array.isArray(data) ? data : [];
}

/**
 * Fetch tasks with status Open that do NOT have any time blocks.
 * Uses role-based filtering similar to fetchTasksForAccount.
 * @param {string} accountId
 * @param {{ user_id?: string, role?: string }} [options]
 */
export async function fetchUnscheduledTasks(accountId, options = {}) {
  if (!accountId) return [];

  const body = {
    account_id: accountId,
    user_id: options.user_id != null ? String(options.user_id) : "",
    role: options.role != null ? String(options.role) : "",
  };

  const resp = await apiFetch(serverInfo.serverUrl + "/getUnscheduledTasks", {
    method: "POST",
    body: JSON.stringify(body),
  });

  checkResponse(resp);
  const data = await resp.json();
  return Array.isArray(data) ? data : [];
}

/**
 * Create a single task (Quick Add row). Uses server createTask.
 */
export async function createTask(payload) {
  const body = {
    account_id: payload.account_id,
    name: String(payload.name || "").trim(),
    due_date: payload.due_date || null,
    amount: payload.amount != null ? Number(payload.amount) : 0,
    repeat_rule: payload.repeat_rule != null ? payload.repeat_rule : null,
    priority: payload.priority != null ? String(payload.priority).trim() : "",
    created_by: payload.created_by != null ? String(payload.created_by) : undefined,
  };
  if (payload.project_id != null && String(payload.project_id).trim() !== "") {
    body.project_id = String(payload.project_id).trim();
  }
  if (payload.notes !== undefined) {
    body.notes = payload.notes != null ? String(payload.notes) : "";
  }
  if (payload.owner !== undefined) {
    body.owner = payload.owner != null ? String(payload.owner) : "";
  }
  if (payload.labels !== undefined) {
    body.labels = payload.labels;
  }
  if (payload.seq !== undefined && payload.seq !== null && payload.seq !== "") {
    body.seq = payload.seq;
  }
  const resp = await apiFetch(serverInfo.serverUrl + "/createTask", {
    method: "POST",
    body: JSON.stringify(body),
  });
  checkResponse(resp);
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || "Create task failed");
  }
  const data = await resp.json();
  return data; // { _id } when server returns created task
}

/**
 * Create multiple tasks in one request (bulk Quick Add).
 * @param {string} accountId
 * @param {Array} tasks
 * @param {string} [createdBy] - user _id of creator (for created_by field)
 */
export async function createTasks(accountId, tasks, createdBy) {
  if (!accountId || !Array.isArray(tasks) || tasks.length === 0) {
    return { created: 0 };
  }

  const resp = await apiFetch(serverInfo.serverUrl + "/createTasks", {
    method: "POST",
    body: JSON.stringify({
      account_id: accountId,
      created_by: createdBy != null && String(createdBy).trim() !== "" ? String(createdBy).trim() : undefined,
      tasks: tasks.map((t) => {
        const row = {
          name: String(t.name || "").trim(),
          due_date: t.due_date || undefined,
          amount: t.amount != null ? Number(t.amount) : 0,
          repeat_rule: t.repeat_rule != null ? t.repeat_rule : null,
          priority: t.priority != null ? String(t.priority).trim() : "",
        };
        if (t.project_id != null && String(t.project_id).trim() !== "") {
          row.project_id = String(t.project_id).trim();
        }
        if (t.labels !== undefined) {
          row.labels = t.labels;
        }
        return row;
      }),
    }),
  });

  checkResponse(resp);
  const data = await resp.json();
  if (!resp.ok) {
    throw new Error(data.error || "Create tasks failed");
  }
  return { created: data.created ?? 0 };
}

// ---- Time blocking APIs ----

export async function fetchTimeBlocksForRange(accountId, startIso, endIso) {
  if (!accountId || !startIso || !endIso) return [];
  const resp = await apiFetch(serverInfo.serverUrl + "/getTimeBlocksForRange", {
    method: "POST",
    body: JSON.stringify({ account_id: accountId, start: startIso, end: endIso }),
  });
  checkResponse(resp);
  const data = await resp.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchMissedTimeBlocks(accountId) {
  if (!accountId) return [];
  const resp = await apiFetch(serverInfo.serverUrl + "/getMissedTimeBlocks", {
    method: "POST",
    body: JSON.stringify({ account_id: accountId }),
  });
  checkResponse(resp);
  const data = await resp.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchCompletedTimeBlocksByCalendarDate(accountId, calendarDate) {
  if (!accountId || !calendarDate) return [];
  const resp = await apiFetch(serverInfo.serverUrl + "/getCompletedTimeBlocksByCalendarDate", {
    method: "POST",
    body: JSON.stringify({ account_id: accountId, calendar_date: calendarDate }),
  });
  checkResponse(resp);
  const data = await resp.json();
  return Array.isArray(data) ? data : [];
}

/**
 * Server-side next free 30-minute slot (working hours, blocks, Google if user_id connected).
 * @param {{ account_id: string, user_id?: string, from_calendar_date?: string, max_days?: number, viewer_time_zone?: string }} params
 * @returns {Promise<{ start_time: string, end_time: string } | null>}
 */
export async function fetchEarliestOpenTimeSlot(params) {
  const account_id = params.account_id != null ? String(params.account_id).trim() : "";
  if (!account_id) return null;

  const body = {
    account_id,
    user_id: params.user_id != null ? String(params.user_id).trim() : "",
    from_calendar_date: params.from_calendar_date != null ? String(params.from_calendar_date).trim() : undefined,
    max_days: params.max_days != null ? Number(params.max_days) : undefined,
    viewer_time_zone:
      params.viewer_time_zone != null && String(params.viewer_time_zone).trim() !== ""
        ? String(params.viewer_time_zone).trim()
        : undefined,
  };

  const resp = await apiFetch(serverInfo.serverUrl + "/findEarliestOpenTimeSlot", {
    method: "POST",
    body: JSON.stringify(body),
  });
  checkResponse(resp);
  const data = await resp.json();
  const slot = data && data.slot != null ? data.slot : null;
  if (!slot || !slot.start_time || !slot.end_time) return null;
  return { start_time: slot.start_time, end_time: slot.end_time };
}

export async function createTimeBlock(payload) {
  const resp = await apiFetch(serverInfo.serverUrl + "/createTimeBlock", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  checkResponse(resp);
  const data = await resp.json();
  return data;
}

export async function updateTimeBlock(payload) {
  const resp = await apiFetch(serverInfo.serverUrl + "/updateTimeBlock", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  checkResponse(resp);
  const data = await resp.json();
  return data;
}

export async function deleteTimeBlock(payload) {
  const resp = await apiFetch(serverInfo.serverUrl + "/deleteTimeBlock", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  checkResponse(resp);
}

/**
 * Close a task (set status to Closed). Used when marking a time block as completed.
 * @param {{ _id: string, account_id: string, user_id?: string, viewer_time_zone?: string }} payload
 */
export async function closeTask(payload) {
  const resp = await apiFetch(serverInfo.serverUrl + "/closeTask", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  checkResponse(resp);
}

/**
 * Preview counts for deleteTask (parent tasks, sub-tasks, time blocks).
 * @param {{ account_id: string, _id?: string, _ids?: string[] }} params
 * @returns {Promise<{ tasks: number, subTasks: number, timeBlocks: number }>}
 */
export async function fetchDeleteTaskImpact(params) {
  const account_id = params.account_id != null ? String(params.account_id).trim() : "";
  if (!account_id) {
    throw new Error("account_id is required");
  }
  const body = { account_id };
  if (Array.isArray(params._ids) && params._ids.length > 0) {
    body._ids = params._ids.map((x) => String(x || "").trim()).filter(Boolean);
  } else if (params._id != null && String(params._id).trim() !== "") {
    body._id = String(params._id).trim();
  } else {
    throw new Error("_id or _ids is required");
  }
  const resp = await apiFetch(serverInfo.serverUrl + "/getDeleteTaskImpact", {
    method: "POST",
    body: JSON.stringify(body),
  });
  checkResponse(resp);
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || "Could not load delete preview");
  }
  return resp.json();
}

/** Human-readable bullet list for delete confirmation dialogs. */
export function formatDeleteTaskImpactSummary(impact) {
  if (!impact || typeof impact.tasks !== "number") {
    return "Related sub-tasks and time blocks will also be permanently deleted.";
  }
  return (
    "Records to delete:\n" +
    `• Tasks (parent): ${impact.tasks}\n` +
    `• Sub-tasks: ${impact.subTasks}\n` +
    `• Time blocks (scheduled / calendar): ${impact.timeBlocks}`
  );
}

/**
 * Delete a task entirely. Used from Unscheduled Tasks backlog.
 * @param {{ _id: string, account_id: string }} payload
 */
export async function deleteTask(payload) {
  const resp = await apiFetch(serverInfo.serverUrl + "/deleteTask", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  checkResponse(resp);
}

/**
 * Reopen a previously closed task (set status back to Open).
 * Used when moving a completed time block back into the schedule.
 * @param {{ _id: string, account_id: string }} payload
 */
export async function reopenTask(payload) {
  const resp = await apiFetch(serverInfo.serverUrl + "/reopenTask", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  checkResponse(resp);
}

/**
 * Update an existing task. Server requires _id, account_id, name, project_id, due_date.
 * Other fields (priority, amount, repeat, notes, owner, etc.) are optional.
 * @param {Object} payload - task fields to update (must include _id, account_id, name, project_id, due_date)
 */
export async function updateTask(payload) {
  const resp = await apiFetch(serverInfo.serverUrl + "/updateTask", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  checkResponse(resp);
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || "Update task failed");
  }
}

/**
 * Bulk update one or more fields across many tasks.
 * Body: { account_id, _ids, seq?, project_id?, priority? }
 * Returns { matched, modified }.
 */
export async function bulkUpdateTasks(payload) {
  const resp = await apiFetch(serverInfo.serverUrl + "/bulkUpdateTasks", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  checkResponse(resp);
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || "Bulk update failed");
  }
  return resp.json();
}

export async function renameTaskLabel(payload) {
  const resp = await apiFetch(serverInfo.serverUrl + "/renameTaskLabel", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  checkResponse(resp);
  const data = await resp.json();
  return typeof data.modifiedCount === "number" ? data.modifiedCount : 0;
}

export async function removeTaskLabel(payload) {
  const resp = await apiFetch(serverInfo.serverUrl + "/removeTaskLabel", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  checkResponse(resp);
  const data = await resp.json();
  return typeof data.modifiedCount === "number" ? data.modifiedCount : 0;
}
